import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, RefreshCw, AlertCircle } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";

export default function OutcomePanel({
  caseId,
  currentCase,
  isReadOnly,
  onCaseRefreshed,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const fetchData = async (forceRegenerate = false) => {
    setLoading(true);
    try {
      const { data: out } = await api.post(
        `/cases/${caseId}/copilot/outcome-suggestion`,
        { force_regenerate: forceRegenerate },
      );
      setData(out);
    } catch (e) {
      toast.error("Could not load outcome suggestion");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const apply = async () => {
    if (!data?.suggested_outcome) return;
    setApplying(true);
    try {
      // PATCH the case to pre-fill the outcome field; do NOT auto-close.
      await api.patch(`/cases/${caseId}`, { outcome: data.suggested_outcome });
      toast.success(
        `Outcome pre-filled: "${data.suggested_outcome}". Closure summary + end date still required to close.`,
      );
      // Audit-log the apply via the dedicated endpoint
      await api.post(`/cases/${caseId}/copilot/apply-suggestion`, {
        suggestion_id: "outcome_suggestion",
        suggestion_text: `Pre-fill outcome: ${data.suggested_outcome}`,
        rationale: data.rationale,
        applied_action_type: "logged_only",
      });
      onCaseRefreshed?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Apply failed";
      toast.error(typeof detail === "string" ? detail : "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="brand-card flex flex-col" data-testid="outcome-panel">
      <div className="flex items-center justify-between border-b border-[color:var(--border-subtle)] px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-[color:var(--page-fg)]">
            Outcome suggestion
          </h3>
          <p className="text-[11px] text-[color:var(--page-fg-muted)]">
            Based on outcomes of similar closed cases
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData(true)}
          disabled={loading}
          data-testid="outcome-regenerate"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Regenerate
        </Button>
      </div>

      <div className="flex-1 px-5 py-4">
        {!data && loading && (
          <p className="py-6 text-center text-sm text-[color:var(--page-fg-muted)]">Computing…</p>
        )}
        {data && !data.llm_available && data.source === "rule_based" && (
          <div
            className="mb-3 flex items-start gap-2 rounded-lg border border-brand-gold/30 bg-brand-gold/10 px-3 py-2 text-xs text-[color:var(--page-fg)]"
            data-testid="outcome-llm-unavailable"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 text-brand-gold" />
            <span>AI rationale unavailable — showing rule-based result.</span>
          </div>
        )}
        {data && !data.suggested_outcome && (
          <p
            className="py-6 text-center text-sm text-[color:var(--page-fg-muted)]"
            data-testid="outcome-no-data"
          >
            Not enough similar closed cases to suggest an outcome yet.
          </p>
        )}
        {data && data.suggested_outcome && (
          <div className="space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                Suggested outcome
              </p>
              <p
                className="mt-1 text-base font-semibold text-[color:var(--page-fg)]"
                data-testid="outcome-suggested"
              >
                {data.suggested_outcome}
              </p>
              <p className="mt-0.5 text-[11px] text-[color:var(--page-fg-muted)]">
                Matched {data.support_count} of {data.total_compared} similar cases
              </p>
            </div>
            <p
              className="rounded-lg bg-[color:var(--surface-2)] p-3 text-xs italic text-[color:var(--page-fg)]"
              data-testid="outcome-rationale"
            >
              "{data.rationale}"
            </p>
            {(data.alternative_outcomes || []).length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                  Alternatives
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {data.alternative_outcomes.map((a, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] px-2 py-0.5 text-[11px] text-[color:var(--page-fg)]"
                      data-testid={`outcome-alt-${i}`}
                    >
                      {a.outcome}{" "}
                      <span className="ml-1 text-[color:var(--page-fg-muted)]">×{a.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <Button
              size="sm"
              onClick={apply}
              disabled={
                isReadOnly ||
                applying ||
                currentCase?.outcome === data.suggested_outcome
              }
              data-testid="outcome-apply"
              className="w-full bg-brand-teal text-white hover:bg-brand-tealDeep"
            >
              <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              {applying
                ? "Applying…"
                : currentCase?.outcome === data.suggested_outcome
                ? "Outcome already set"
                : "Pre-fill outcome on this case"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
